/**
 * 平台收費前端服務。
 * - 平台管理端：/api/ee/platform-billing/*
 * - 組織管理端：/api/ee/my-bills/*
 * axios 攔截器自動附加 Authorization + X-Txn-Token，服務層不手動傳 token。
 */
import axios from '../api/axios';

const ADMIN = '/api/ee/platform-billing';
const MINE = '/api/ee/my-bills';

export type BillStatus = 'draft' | 'unpaid' | 'paid' | 'void';

export interface BillItem {
  name: string;
  amount: number;              // 未稅金額（折抵為負）
  // 金額模型（財務會計基礎 §1）：fee = 未稅費用；discount = 未稅折抵。省略時後端預設 fee。
  kind?: string;
  promo_incl?: number | null;  // 含稅優惠價（帶入行才有·建議優惠價用）
}

export interface RemitConfig {
  bank_name: string;
  bank_code: string;
  branch: string;
  account_name: string;
  account_no: string;
}

export interface Bill {
  id: number;
  tenant_org_id: number;
  tenant_org_name?: string | null;
  bill_no?: string | null;
  title: string;
  period_start?: string | null;
  period_end?: string | null;
  amount: number;                    // ⑤ 應繳總額
  // 稅基（財務會計基礎 §1·合計區顯示用）
  tax_type?: number;
  subtotal_amount?: number | null;
  promo_total?: number | null;
  promo_manual?: boolean;
  sales_amount?: number | null;
  tax_amount?: number | null;
  items: BillItem[];
  due_date?: string | null;
  status: BillStatus;
  // 系統電子發票狀態（S5·平台列表「發票」欄顯示上傳狀態）
  einvoice_status?: string | null;          // issued / void
  einvoice_upload_status?: string | null;   // pending / uploaded / failed
  einvoice_upload_error?: string | null;
  issued_at?: string | null;
  issued_by?: number | null;
  paid_at?: string | null;
  paid_amount?: number | null;
  paid_note?: string | null;
  confirmed_by?: number | null;
  invoice_no?: string | null;
  void_reason?: string | null;
  voided_at?: string | null;
  created_at?: string | null;
  remit?: RemitConfig | null;
}

export interface BillCreate {
  tenant_org_id: number;
  title: string;
  period_start?: string | null;
  period_end?: string | null;
  items: BillItem[];
  due_date?: string | null;
  invoice_no?: string | null;
  // ④ 優惠價（含稅總額）：promo_manual=true 時以 promo_total 為準（null = 無優惠價）；
  // false 時後端自動算建議優惠價。
  promo_total?: number | null;
  promo_manual?: boolean;
}

export interface TenantInvoiceProfile {
  tenant_org_id: number;
  org_name?: string | null;
  tax_id?: string | null;
  invoice_title?: string | null;
}

export type BillUpdate = Partial<Omit<BillCreate, 'tenant_org_id'>>;

export interface IssueResult {
  bill: Bill;
  email_sent: number;
  email_skipped: number;
  line_sent: number;
  line_skipped: number;
  failed: number;
  no_admin: boolean;
}

export interface NoticeLog {
  id: number;
  kind: string;
  bill_id?: number | null;
  notification_id?: number | null;
  tenant_org_id?: number | null;
  user_id?: number | null;
  channel: string;
  status: string;
  error?: string | null;
  sent_at?: string | null;
}

export interface UsageLine {
  name: string;
  amount: number;
}

export interface TenantBills {
  items: Bill[];
  friend_url?: string | null;
}

// ── 平台管理端 ──────────────────────────────────────────────

export const getRemit = async (): Promise<RemitConfig> =>
  (await axios.get(`${ADMIN}/remit`)).data;

export const saveRemit = async (data: RemitConfig): Promise<RemitConfig> =>
  (await axios.put(`${ADMIN}/remit`, data)).data;

export const listBills = async (params?: {
  tenant_org_id?: number; status?: BillStatus; year?: number; month?: number;
}): Promise<Bill[]> => (await axios.get(`${ADMIN}/bills`, { params })).data;

export const getBill = async (id: number): Promise<Bill> =>
  (await axios.get(`${ADMIN}/bills/${id}`)).data;

export const getBillNotices = async (id: number): Promise<NoticeLog[]> =>
  (await axios.get(`${ADMIN}/bills/${id}/notices`)).data;

export interface RecipientPreview {
  admins: number;
  email: number;
  line: number;
  attachments: number;
}

export interface Attachment {
  id: number;
  file_id: number;
  filename: string;
  size_bytes: number;
  uploaded_by?: number | null;
  uploaded_at?: string | null;
}

export const getRecipientsPreview = async (id: number): Promise<RecipientPreview> =>
  (await axios.get(`${ADMIN}/bills/${id}/recipients-preview`)).data;

export const getLineUsage = async (year: number, month: number): Promise<{ count: number }> =>
  (await axios.get(`${ADMIN}/line-usage`, { params: { year, month } })).data;

/** 平台 LINE OA 是否已設定（S5：決定是否顯示廣播功能）。 */
export const getOaStatus = async (): Promise<{ configured: boolean; friend_url: string | null }> =>
  (await axios.get(`${ADMIN}/oa-status`)).data;

export const computeUsage = async (
  tenant_org_id: number, year: number, month: number,
): Promise<UsageLine> => (await axios.post(`${ADMIN}/compute-usage`, { tenant_org_id, year, month })).data;

// ── 租戶發票資料（統編 + 抬頭·B3）─────────────────────────────
export const getTenantInvoiceProfile = async (orgId: number): Promise<TenantInvoiceProfile> =>
  (await axios.get(`${ADMIN}/tenants/${orgId}/invoice-profile`)).data;

export const saveTenantInvoiceProfile = async (
  orgId: number, data: { tax_id?: string | null; invoice_title?: string | null },
): Promise<TenantInvoiceProfile> =>
  (await axios.put(`${ADMIN}/tenants/${orgId}/invoice-profile`, data)).data;

export const createBill = async (data: BillCreate): Promise<Bill> =>
  (await axios.post(`${ADMIN}/bills`, data)).data;

export const updateBill = async (id: number, data: BillUpdate): Promise<Bill> =>
  (await axios.put(`${ADMIN}/bills/${id}`, data)).data;

export const deleteBill = async (id: number): Promise<void> => {
  await axios.delete(`${ADMIN}/bills/${id}`);
};

export const issueBill = async (id: number): Promise<IssueResult> =>
  (await axios.post(`${ADMIN}/bills/${id}/issue`)).data;

export const payBill = async (
  id: number, body: { paid_amount: number; paid_date?: string; paid_note?: string },
): Promise<IssueResult> => (await axios.post(`${ADMIN}/bills/${id}/pay`, body)).data;

export const voidBill = async (id: number, reason: string): Promise<IssueResult> =>
  (await axios.post(`${ADMIN}/bills/${id}/void`, { reason })).data;

export const renotifyBill = async (id: number): Promise<IssueResult> =>
  (await axios.post(`${ADMIN}/bills/${id}/renotify`)).data;

/** 系統公告 LINE 廣播（同一則只能一次）。由系統通知頁勾選後呼叫。 */
export const broadcastAnnouncement = async (notificationId: number): Promise<{ sent: boolean }> =>
  (await axios.post(`${ADMIN}/announcements/${notificationId}/broadcast`)).data;

// ── 附件（平台端）─────────────────────────────────────────────

export const listAttachments = async (billId: number): Promise<Attachment[]> =>
  (await axios.get(`${ADMIN}/bills/${billId}/attachments`)).data;

export const uploadAttachment = async (billId: number, file: File): Promise<Attachment> => {
  const fd = new FormData();
  fd.append('file', file);
  // 覆蓋預設 json content-type，讓瀏覽器自帶 multipart boundary
  return (await axios.post(`${ADMIN}/bills/${billId}/attachments`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};

export const deleteAttachment = async (billId: number, attId: number): Promise<void> => {
  await axios.delete(`${ADMIN}/bills/${billId}/attachments/${attId}`);
};

export const downloadAttachment = async (billId: number, attId: number): Promise<Blob> =>
  (await axios.get(`${ADMIN}/bills/${billId}/attachments/${attId}/download`,
    { responseType: 'blob' })).data;

// ── 組織管理端 ──────────────────────────────────────────────

export const getMyBills = async (): Promise<TenantBills> =>
  (await axios.get(`${MINE}/`)).data;

/** 我的租戶發票資料（唯讀·B3）。 */
export const getMyInvoiceProfile = async (): Promise<TenantInvoiceProfile> =>
  (await axios.get(`${MINE}/invoice-profile`)).data;

export const getMyBill = async (id: number): Promise<Bill> =>
  (await axios.get(`${MINE}/${id}`)).data;

export const listMyBillAttachments = async (billId: number): Promise<Attachment[]> =>
  (await axios.get(`${MINE}/${billId}/attachments`)).data;

export const downloadMyBillAttachment = async (billId: number, attId: number): Promise<Blob> =>
  (await axios.get(`${MINE}/${billId}/attachments/${attId}`, { responseType: 'blob' })).data;
